"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  prefix: string;
  suffix: string;
  target: number;
  decimals: number;
  label: string;
  caption: string;
  accentPosition: "prefix" | "suffix";
};
const stats: Stat[] = [{
  prefix: "",
  suffix: "%",
  target: 45,
  decimals: 0,
  label: "Operational efficiency",
  caption: "Reduction in manual data entry tasks",
  accentPosition: "suffix"
}, {
  prefix: "",
  suffix: "x",
  target: 3.5,
  decimals: 1,
  label: "Growth velocity",
  caption: "Increase in project delivery speed",
  accentPosition: "suffix"
}, {
  prefix: "+",
  suffix: "%",
  target: 32,
  decimals: 0,
  label: "Client retention",
  caption: "Improvement in long-term partnerships",
  accentPosition: "prefix"
}];
function CountUpTile({
  stat,
  index
}: {
  stat: Stat;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4,
    once: true
  });
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;
    const delay = index * 120;
    const duration = 1400;
    let rafId = 0;
    let startTime = 0;
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    const timeoutId = window.setTimeout(() => {
      const tick = (now: number) => {
        if (!startTime) startTime = now;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOut(progress);
        setValue(stat.target * eased);
        if (progress < 1) {
          rafId = window.requestAnimationFrame(tick);
        } else {
          setValue(stat.target);
        }
      };
      rafId = window.requestAnimationFrame(tick);
    }, delay);
    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [isVisible, index, stat.target]);
  const formatted = value.toFixed(stat.decimals);
  return <div ref={ref} data-index={index} className="group rounded-lg border border-border bg-card p-10 shadow-md transition-all duration-[280ms] ease-out hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg">
      <div className="font-default flex items-baseline leading-[0.95] tracking-tight text-foreground" style={{
      fontSize: "clamp(3.5rem, 7vw, 6rem)",
      fontWeight: 500,
      letterSpacing: "-0.04em"
    }}>
        {stat.accentPosition === "prefix" && stat.prefix && <span className="text-primary">{stat.prefix}</span>}
        {stat.accentPosition !== "prefix" && stat.prefix && <span>{stat.prefix}</span>}
        <span>{formatted}</span>
        {stat.accentPosition === "suffix" && <span className="text-primary">{stat.suffix}</span>}
        {stat.accentPosition !== "suffix" && <span>{stat.suffix}</span>}
      </div>
      <h4 className="font-default mt-6 text-base font-semibold text-foreground md:text-lg">
        {stat.label}
      </h4>
      <p className="font-default mt-2 text-sm text-muted-foreground">
        {stat.caption}
      </p>
    </div>;
}
export default function Impact() {
  return <section data-section-id="6651" id="business-impact-statistics" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="font-default text-foreground" style={{
          fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
          fontWeight: 600,
          letterSpacing: "-0.025em",
          lineHeight: 1.06
        }}>
            Proven results,{" "}
            <em className="font-normal italic text-primary">quantified</em>.
          </h2>
          <p className="font-default mt-5 text-base text-muted-foreground md:text-lg">
            Key performance metrics achieved by our partners within the first quarter of implementation.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {stats.map((stat, i) => <CountUpTile key={i} stat={stat} index={i} />)}
        </div>
      </div>
    </section>;
}